/* eslint-disable react/prop-types */
import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { API } from '@/App';
import AdminLayout from '@/components/layout/AdminLayout';
import { Loader2, Map, ChevronDown } from 'lucide-react';

/**
 * Territory Heatmaps — Phase 5.1.
 *
 * One calm consolidated heatmap page. Dropdown to switch between four
 * deterministic dimensions:
 *   - ecosystem_perception · cells = lead count
 *   - centre_band         · cells = % of leads
 *   - ecosystem_rss       · cells = lead count
 *   - ecosystem_velocity  · cells = forward velocity %
 *
 * Calm earth-tone gradient (no rainbow). Hover shows numeric tooltip.
 * No sales-language. Read-only.
 */
export default function TerritoryHeatmapsPage() {
  const [dims, setDims] = useState([]);
  const [dim, setDim] = useState('ecosystem_perception');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/terra-growth/heatmaps/dimensions`)
      .then(r => setDims(r.data?.dimensions || []))
      .catch(() => setDims([]));
  }, []);

  useEffect(() => {
    setLoading(true);
    axios.get(`${API}/terra-growth/heatmaps?dimension=${dim}`)
      .then(r => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [dim]);

  const activeDim = dims.find(d => d.key === dim) || {};

  return (
    <AdminLayout>
      <div className="terra-canvas">
      <div data-testid="territory-heatmaps-page" className="terra-page-pad max-w-7xl mx-auto px-8 py-12">
        <Header dims={dims} dim={dim} setDim={setDim} activeDim={activeDim} />

        {loading ? (
          <div className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            <Loader2 className="w-5 h-5 mx-auto animate-spin" /> Composing the territory…
          </div>
        ) : !data ? (
          <div data-testid="heatmaps-error"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            Could not load this view.
          </div>
        ) : (data.row_keys?.length || 0) === 0 ? (
          <div data-testid="heatmaps-empty"
               className="rounded-2xl border border-[#EBE8E3] bg-white p-16 text-center text-[#8A8681]">
            No data in this view yet — the territory is quiet here.
          </div>
        ) : (
          <HeatmapGrid data={data} />
        )}

        <p className="text-[11px] text-[#8A8681] italic max-w-2xl mt-10 pt-6 border-t border-[#F4F2EE]">
          Heatmaps are read-only territory snapshots. Calm earth-tones reflect
          density, not urgency. Hover any cell for its underlying number.
        </p>
      </div>
      </div>
    </AdminLayout>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Header (with dimension dropdown)
// ═══════════════════════════════════════════════════════════════════

function Header({ dims, dim, setDim, activeDim }) {
  return (
    <header className="mb-10 flex flex-wrap items-end justify-between gap-4">
      <div className="space-y-3 max-w-2xl">
        <p className="text-[10px] uppercase tracking-[0.22em] text-[#8C6239] flex items-center gap-2">
          <Map className="w-3 h-3" /> Terra Growth OS™ · Visualization
        </p>
        <h1 className="text-4xl font-['Outfit'] font-light text-[#2C2A29] tracking-tight">
          Territory Heatmaps
        </h1>
        <p className="text-sm text-[#8A8681] font-['Work_Sans'] leading-relaxed">
          Where the territory holds density, where readiness is forming, where
          movement is alive. A calm look across ecosystems, centres, and rungs.
        </p>
      </div>
      <div className="relative">
        <label className="text-[10px] uppercase tracking-[0.18em] text-[#8A8681] block mb-1.5">
          View
        </label>
        <div className="relative inline-block">
          <select data-testid="heatmap-dimension-select"
                  value={dim}
                  onChange={e => setDim(e.target.value)}
                  className="appearance-none text-sm bg-white border border-[#D8D4CE] rounded-full pl-4 pr-9 py-2 text-[#2C2A29] focus:outline-none focus:border-[#4A5D4E] cursor-pointer min-w-[260px]">
            {dims.map(d => (
              <option key={d.key} value={d.key} data-testid={`heatmap-dim-${d.key}`}>
                {d.label}
              </option>
            ))}
          </select>
          <ChevronDown className="w-3.5 h-3.5 text-[#8A8681] absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
        {activeDim?.row_axis && (
          <p className="text-[10px] text-[#8A8681] mt-1.5">
            {activeDim.row_axis} × {activeDim.col_axis}
          </p>
        )}
      </div>
    </header>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Heatmap grid
// ═══════════════════════════════════════════════════════════════════

function HeatmapGrid({ data }) {
  const rows = data.row_keys || [];
  const cols = data.col_keys || [];
  const max = Math.max(1, Number(data.max_value) || 1);
  const unit = data.value_unit || '';

  const cellMap = useMemo(() => {
    const m = {};
    (data.cells || []).forEach(c => { m[`${c.row}::${c.col}`] = c; });
    return m;
  }, [data.cells]);

  return (
    <section data-testid="heatmap-grid"
             className="rounded-2xl border border-[#EBE8E3] bg-white p-6 overflow-x-auto">
      <table className="w-full" data-testid="heatmap-table">
        <thead>
          <tr>
            <th className="text-left text-[10px] uppercase tracking-[0.16em] text-[#8A8681] py-2 pr-4 sticky left-0 bg-white">
              {data.row_axis}
            </th>
            {cols.map(c => (
              <th key={c} data-testid={`heatmap-col-${c}`}
                  className="text-center text-[10px] uppercase tracking-[0.14em] text-[#8A8681] py-2 px-2 font-normal min-w-[78px]">
                {(data.col_labels && data.col_labels[c]) || c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map(rowKey => (
            <tr key={rowKey} data-testid={`heatmap-row-${rowKey}`}>
              <th className="text-left text-[12px] text-[#2C2A29] font-normal py-2 pr-4 sticky left-0 bg-white max-w-[180px] truncate">
                {(data.row_labels && data.row_labels[rowKey]) || rowKey}
              </th>
              {cols.map(colKey => {
                const cell = cellMap[`${rowKey}::${colKey}`];
                return (
                  <Cell key={colKey} cell={cell} max={max} unit={unit}
                        rowKey={rowKey} colKey={colKey} />
                );
              })}
            </tr>
          ))}
        </tbody>
      </table>
      <Legend max={max} unit={unit} />
    </section>
  );
}

// ═══════════════════════════════════════════════════════════════════
// Cell
// ═══════════════════════════════════════════════════════════════════

function Cell({ cell, max, unit, rowKey, colKey }) {
  const v = cell?.value;
  const intensity = v == null ? 0 : Math.min(1, Math.abs(Number(v)) / max);
  // Calm earth-tone gradient — light parchment → deep sage.
  const bg = v == null
    ? '#FAF8F4'
    : intensity === 0
      ? '#FAF8F4'
      : intensityColor(intensity);
  const text = intensity > 0.55 ? '#FFFFFF' : '#2C2A29';
  const display = v == null ? '—' : (unit === '%' ? `${v}%` : v);
  const tooltip = v == null
    ? `${rowKey} · ${colKey} · no data`
    : `${rowKey} · ${colKey} · ${display}${cell.sample ? ` · sample ${cell.sample}` : ''}${cell.confidence ? ` · ${cell.confidence}` : ''}`;
  return (
    <td data-testid={`heatmap-cell-${rowKey}-${colKey}`}
        className="p-1">
      <div title={tooltip}
           className="rounded-md flex items-center justify-center h-10 transition-colors duration-200 cursor-default font-['Work_Sans'] tabular-nums text-[12px]"
           style={{ background: bg, color: text }}>
        {display}
      </div>
    </td>
  );
}

// Earth-tone gradient: parchment → sage → deep sage.
// Uses calm earth-tone palette (no red/rainbow) per design spec.
function intensityColor(t) {
  // 3-stop gradient: #F2EAD8 (parchment) → #B5C2A8 (sage) → #4A5D4E (deep sage)
  const stops = [
    { at: 0,   r: 250, g: 248, b: 244 },  // very light parchment
    { at: 0.4, r: 232, g: 220, b: 196 },  // warm parchment
    { at: 0.7, r: 181, g: 194, b: 168 },  // sage
    { at: 1,   r: 74,  g: 93,  b: 78  },  // deep sage
  ];
  for (let i = 1; i < stops.length; i++) {
    if (t <= stops[i].at) {
      const a = stops[i - 1];
      const b = stops[i];
      const span = b.at - a.at;
      const local = span > 0 ? (t - a.at) / span : 0;
      const r = Math.round(a.r + (b.r - a.r) * local);
      const g = Math.round(a.g + (b.g - a.g) * local);
      const bl = Math.round(a.b + (b.b - a.b) * local);
      return `rgb(${r}, ${g}, ${bl})`;
    }
  }
  return `rgb(74, 93, 78)`;
}

// ═══════════════════════════════════════════════════════════════════
// Legend
// ═══════════════════════════════════════════════════════════════════

function Legend({ max, unit }) {
  const ticks = [0, 0.25, 0.5, 0.75, 1];
  return (
    <div data-testid="heatmap-legend"
         className="flex items-center gap-3 mt-6 pt-4 border-t border-[#F4F2EE]">
      <span className="text-[10px] uppercase tracking-[0.16em] text-[#8A8681]">density</span>
      <div className="flex h-2 rounded-full overflow-hidden flex-1 max-w-[280px]">
        {ticks.map((t, i) => (
          <div key={i} style={{ background: intensityColor(t), flex: 1 }} />
        ))}
      </div>
      <span className="text-[10px] tabular-nums text-[#8A8681]">
        0 {unit && '— '}{unit !== '%' ? max : `${max}%`}
      </span>
    </div>
  );
}
